/**
 * @file        submit-signed-post.js - utilities for submitting data to the signed-post library
 * @author      Wes Garland, wes@distributive.network
 * @date        July 2024
 */
'use strict';

const { reallyJustFetch }  = require('./just-fetch');
const { serverTimeAdjust } = require('./server-time')
const { randStr21 }        = require('dcp/protocol/utils');
const { assert }           = require('dcp/common/dcp-assert');
const { ServerSideError }  = require('./http');

/**
 * send a query to the signed-post.js backend library and handle all of the error conditions nicely.
 * @param {object} authorizer      a Keystore or PrivateKey that is used to sign the message; this
 *                                 becomes the message owner in ethereum-speak.
 * @param {object} messageBody     the body of the message to send, formatted for decoding by the dcp
 *                                 signed-post library. Note - messageBody.expiry should be in local
 *                                 time; it will be automatically converted to server time before the
 *                                 post is made.
 * @param {objects} options        [optional] see submitPost
 *
 * @returns {object} that resolves to the result of the query, or an instance of error if there was a
 *                   remote error indicated by success: false.
 */
exports.submitSignedPost = async function utils$$submitSignedPost(authorizer, messageBody, options)
{
  var result;

  try
  {
    if (options?.busy)
      options.busy();

    const endpointUrl = new URL(messageBody.endpoint);
    const timeServiceLocation = exports.determineTimeServiceLocation(endpointUrl, options)
    const adjMessageBody = Object.assign({}, messageBody);
    const adjExpiry = await serverTimeAdjust(messageBody.expiry, timeServiceLocation, endpointUrl.href);
    adjMessageBody.expiry = Math.ceil(adjExpiry);
    const signedMessage = await authorizer.makeSignedMessage(adjMessageBody);
    const xhr = await exports.submitPost(adjMessageBody.endpoint, signedMessage, 'application/json', options);
    if (xhr.getResponseHeader('content-type') !== 'application/json')
      throw new Error(`server sent response with invalid content-type ${xhr.getResponseHeader('content-type')}`);

    result = JSON.parse(xhr.responseText);
    if (result.success === true)
      return result;
  }
  finally
  {
    if (options?.unbusy)
      options.unbusy();
  }

  if (!result.error)
  {
    throw new Error('Server sent invalid response: ' + JSON.stringify(result));
  }

  return new ServerSideError(result.error, messageBody.endpoint, 'XHR POST');
}

/**
 * Submit a POST message using justFetch::reallyJustFetch(). This API works out the various encoding
 * details, but leaves the network-foo and error-handing up to reallyJustFetch.
 *
 * The encoding type is determied by the contentType parameter. Supported encoding types are:
 * - application/x-www-form-urlencoded: "regular" HTML form submission. Each property is considered to be
 *                                       the name of a form element, and the String(value) is the value
 *                                       of the element.
 * - application/json: the body of the POST message is a JSON string
 * - application/x-kvin: the body of the POST message is a KVIN string; the specific KVIN encoding is
 *                       determined by options.KVIN
 * - multipart/form-data: HTML form submission that includes one or more file uploads. Each property of
 *                        the payload is treated like a separate form element, the same as "regular"
 *                        HTML submission, unless there is a filename property. In that case, that
 *                        element is treated like an <INPUT type="file"> element. In both cases, the
 *                        value of the part is given by the body property. When dealing with files,
 *                        the body can be a JS string, a Buffer, or a TypedArray.
 *
 * @note *** only application/json has been tested. /wg aug 2024
 *
 * @param {URL|String}    url          the URL to POST to. Passed directly to reallyJustFetch().
 * @param {string|Object} payload      the payload to post. Payloads which are strings are used literally.
 * @param {string}        contentType  the type of POST we are doing. See code for shortcuts.
 * @param {Object}        options      [optional] - an options object with follow properties:
 *                                     - reallyJustFetch: passed as-is to reallyJustFetch as options
 *                                     - busy:            callback invoked before network activity
 *                                     - unbusy:          callback invoked after network activity
 * @returns the return value of reallyJustFetch (instance of XHR)
 */
exports.submitPost = async function utils$$submitPost(url, payload, contentType, options)
{
  var body;
  var KVIN = options?.KVIN;
  const headers = {};

  assert(url, payload, contentType);
  assert(typeof options === 'object' || typeof options === 'undefined');

  /* contentType shortcuts */
  if (contentType === 'urlencoded' || contentType === undefined)
    contentType = 'application/x-www-form-urlencoded';
  else if (contentType === 'multipart')
    contentType = 'multipart/form-data';
  else if (contentType === 'json')
    contentType = 'application/json';
  else if (contentType === 'kvin')
    contentType = 'application/x-kvin';

  Object.assign(headers, options?.headers);
  headers['content-type'] = headers['content-type'] || contentType;

  if (typeof payload !== 'object')
    body = String(payload);
  else
  {
    switch(contentType)
    {
      default:
        throw new Error(`Cannot post with content type '${contentType}'`);
      case 'application/x-www-form-urlencoded':
      {
        const entries = Object.entries(payload);
        body = entries.map((kvp) => `${encodeURIComponent(kvp[0])}=${encodeURIComponent(kvp[1])}`).join('&');
        break;
      }
      case 'application/json':
        body = JSON.stringify(payload);
        break;
      case 'application/x-kvin':
        body = KVIN.stringify(payload);
        break;
      case 'multipart/form-data':
      {
        const boundary = '==_-=-' + Date.now() + '-' + randStr21();
        const parts = [];

        for (let param in payload)
        {
          if (typeof payload[param] === 'string')
          {
            payload[param] = {
              options: { headers: { 'content-disposition': `form-data; name="${param}"` } },
              body: { [param]: payload[param] },
            };
          }

          if (!payload[param].options)
            payload[param].options = { headers: {} }
          if (payload[param].filename && !payload[param].options.headers['content-disposition'])
            payload[param].options.headers['content-disposition'] = `form-data; name="${param}" filename="${payload[param].filename}"`;

          let part = Object.entries(payload[param].options?.headers).map(entry => {
            const [headerName, value] = entry;
            return `${headerName}: ${value}`;
          }).join('\r\n');
          assert(part); /* assert implies no headers were generated => bad input */

          if (!payload[param].filename)
            part += '\r\n' + encodeURIComponent(payload[param].body);
          else
          {
            part += 'content-transfer-encoding: base64'; /** @todo: use 8-bit transport, HTTP supports it! /wg aug 2024 */
            part += '\r\n' + btoa(payload[param].body);
          }
        }
        body = parts.join('\r\n--' + boundary + '\r\n');
        body += '--' + boundary + '--';
        headers['content-length'] = body.length;
        headers['content-type'] += `; boundary=${boundary}`;
        break;
      }
    }
  }

  const rjfOptions = Object.assign({}, options?.reallyJustFetch, { body, headers, method: 'POST' });
  try
  {
    if (options?.busy)
      options?.busy();
    const xhr = await reallyJustFetch(url, rjfOptions);
    return xhr;
  }
  finally
  {
    if (options?.unbusy)
      options.unbusy();
  }
}

const ErrorCtors = {
  'Error':          Error,
  'TypeError':      TypeError,
  'SyntaxError':    SyntaxError,
  'ReferenceError': ReferenceError,
}

/**
 * Submit a post message to the server and decode the response. The response from the server can be
 * an Error-like object (success = false) or may contain an Error-like object named error.  If either of
 * these are present in the response, they are re-written to become instances of Error.
 *
 * @param {URL|String}    url          the URL to POST to. In browsers, strings which start with '/' are
 *                                     relative to window.origin.
 * @param {string|Object} payload      the payload to post. Payloads which are strings are used literally.
 *                                     Payloads which are objects are serialized based on the contentType.
 * @param {string}        contentType  [optional] the type of POST we are doing. See submitPost code for
 *                                     shortcuts. The default is application/x-www-form-urlencoded.
 * @param {Object}        options      [optional] - passed directly to submitPost
 *
 * Basically the same as utils.submitPost, but with a more rigid API and web-dev-friendly error handling.
 * Post semantics are similar to the signed-post code in terms of error handling, but there is no
 * message signing.
 */
exports.postQuery = async function utils$$postQuery(url, obj, contentType, options)
{
  var result;

  if (arguments.length === 3 && typeof contentType === 'object')
  {
    options = arguments[3];
    contentType = undefined;
  }

  const xhr = await exports.submitPost(url, obj, contentType || 'application/x-www-form-urlencoded', options);
  if (xhr.getResponseHeader('content-type') !== 'application/json')
    throw new Error(`server sent response with invalid content-type ${xhr.getResponseHeader('content-type')}`);

  try
  {
    result = JSON.parse(xhr.responseText);
  }
  catch(error)
  {
    throw new Error('response had invalid JSON; ' + error.message);
  }

  if (result.success === false) /* error processing request */
  {
    const ErrorCtor = ErrorCtors[result.name] || Error;
    const error = new ErrorCtor(result.message);
    Object.assign(error, result);
    return error;
  }

  if (result.error) /* request resulted in an error */
  {
    const ErrorCtor = ErrorCtors[result.error.name] || Error;
    const error = new ErrorCtor(result.error.message);
    Object.assign(error, result.error);
    result.error = error;
  }

  return result;
}

/**
 * Determine the location of the time service that will be used for a given submitPost endpoint.
 *
 * @param { URL | string }  endpoint
 * @param {object} options        [optional] see submitPost
 * @returns { false | string }
 */
exports.determineTimeServiceLocation = function determineTimeServiceLocation(endpoint, options)
{
  if (typeof endpoint === 'string')
    endpoint = new URL(endpoint);

  return false
    || (options?.timeServiceLocation && String(options.timeServiceLocation))
    || (endpoint.origin + endpoint.pathname.replace(/\/?[^/]*$/, '/time'));
}
